import { AuthenticationManager } from '@frontend/authentication-v2';
import { MySettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { useEffect, useState } from 'react';

interface ViewProps {
    authenticated: boolean;
    onLoginSucceeded: () => void;

    overrideEulaUrl?: string | null;
    shouldAcceptEula?: boolean;
    user?: IUser | null;
    acceptEulaCallback?: () => void;
}

const useAuthentication = (): ViewProps => {
    const [authenticated, setAuthenticated] = useState<boolean>(AuthenticationManager.getInstance().hasValidToken());
    const [eulaInfo, setEulaInfo] = useState<{ user: IUser; override_eula_url: string | null; eula_acceptance_required: boolean } | null>(null);
    const shouldAcceptEula = eulaInfo?.user?.eula_accepted_timestamp === null && eulaInfo?.eula_acceptance_required;

    useEffect(() => {
        if (authenticated && !eulaInfo) {
            getEulaInfo().then((info) => {
                if (info !== undefined) {
                    setEulaInfo(info);
                } else setAuthenticated(false);
            });
        }
    }, [authenticated, eulaInfo]);

    const acceptEulaCallback = (): void => {
        getEulaInfo().then((info) => {
            if (info !== undefined) {
                setEulaInfo(info);
            } else setAuthenticated(false);
        });
    };

    const onLoginSucceeded = (): void => {
        setAuthenticated(AuthenticationManager.getInstance().hasValidToken());
    };

    return {
        authenticated,
        onLoginSucceeded,
        overrideEulaUrl: eulaInfo?.override_eula_url,
        shouldAcceptEula,
        user: eulaInfo?.user,
        acceptEulaCallback
    };
};

export default useAuthentication;

async function getEulaInfo(): Promise<{ user: IUser; override_eula_url: string | null; eula_acceptance_required: boolean } | undefined> {
    const token = await AuthenticationManager.getInstance().getTokenWhenReady();
    if (!token || token.entity_type !== 'user') {
        AuthenticationManager.getInstance().logout();
        return undefined;
    }
    const accSettings = await MySettingClient.fetchMyAccountSettings();
    const user = await UserClient.fetchMyself();

    return {
        user,
        override_eula_url: (accSettings.results.find((s) => s.key === SettingKey.OVERRIDE_EULA_URL)?.value as string) || null,
        eula_acceptance_required: (accSettings.results.find((s) => s.key === SettingKey.EULA_ACCEPTANCE_REQUIRED)?.value as boolean) || false
    };
}
